// sc-quick-order.js - Dealer Workbench Quick Order direct-cart shell.
// Browser parses SKU/qty only; Workbench app proxy remains authoritative for SKU resolution.
// Valid resolved variants are added to Shopify cart through the standard Ajax Cart API.
(() => {
  'use strict';

  const root = document.querySelector('[data-qo-root]');
  if (!root) return;

  const config = window.SC_WORKBENCH || {};
  const authorized = root.dataset.qoAuthorized === 'true';
  const state = {
    mode: 'sku',
    raw: '',
    rows: [],
    lastResult: null,
    busy: false,
  };

  const qs = (selector, ctx = root) => ctx.querySelector(selector);
  const qsa = (selector, ctx = root) => Array.from(ctx.querySelectorAll(selector));
  const clean = (value) => (value == null ? '' : String(value).trim());
  const escapeHtml = (value) => String(value == null ? '' : value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  function proxyBase() {
    return clean(config.proxyBase || '/apps/workbench').replace(/\/$/, '') || '/apps/workbench';
  }

  function cartRoot() {
    const rootPath = window.Shopify?.routes?.root || '/';
    return rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
  }


  function parseHTML(html) {
    return new DOMParser().parseFromString(String(html || ''), 'text/html');
  }

  async function fetchCartState() {
    const response = await fetch(`${cartRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart state ${response.status}`);
    return response.json();
  }

  async function fetchCartSections() {
    const rootPath = cartRoot();
    const separator = rootPath.includes('?') ? '&' : '?';
    const response = await fetch(`${rootPath}${separator}sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart sections ${response.status}`);
    return response.json();
  }

  function publishCartUpdate(cartState) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-order',
          cartData: cartState,
          variantId: null,
        });
      }
    } catch (error) {
      console.warn('[quick-order] cart publish failed', error);
    }
  }

  function syncCartDrawerEmptyState(itemCount) {
    const count = Number(itemCount || 0);
    const isEmpty = count === 0;
    const drawer = document.querySelector('cart-drawer');
    const drawerItems = document.querySelector('cart-drawer-items');
    const drawerInner = drawer?.querySelector('.drawer__inner');
    const drawerFooter = drawer?.querySelector('.drawer__footer');
    const emptyWrap = drawer?.querySelector('.drawer__inner-empty');

    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
    if (drawerInner) drawerInner.classList.toggle('is-empty', isEmpty);
    if (drawerFooter) drawerFooter.classList.toggle('is-empty', isEmpty);
    if (emptyWrap) emptyWrap.hidden = !isEmpty;
  }

  function updateCartIconBubble(sectionHtml) {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon || !sectionHtml) return;
    const section = parseHTML(sectionHtml).querySelector('.shopify-section');
    cartIcon.innerHTML = section ? section.innerHTML : sectionHtml;
  }

  async function refreshCartUiAfterAdd(trigger) {
    const drawer = document.querySelector('cart-drawer');
    const cartIcon = document.querySelector('#cart-icon-bubble');
    let cartState = null;
    let sections = null;

    try {
      cartState = await fetchCartState();
      publishCartUpdate(cartState);
    } catch (error) {
      console.warn('[quick-order] cart state refresh failed', error);
    }

    try {
      sections = await fetchCartSections();
    } catch (error) {
      console.warn('[quick-order] cart sections refresh failed', error);
    }

    if (drawer && sections?.['cart-drawer'] && typeof drawer.renderContents === 'function') {
      try {
        if (trigger && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);
        drawer.renderContents({
          id: cartState?.token || cartState?.id || Date.now(),
          sections,
        });
        syncCartDrawerEmptyState(cartState?.item_count || 0);
        return true;
      } catch (error) {
        console.warn('[quick-order] cart drawer render failed', error);
      }
    }

    if (sections?.['cart-icon-bubble']) {
      updateCartIconBubble(sections['cart-icon-bubble']);
    }

    if (drawer && typeof drawer.open === 'function') {
      syncCartDrawerEmptyState(cartState?.item_count || 0);
      drawer.open(trigger || cartIcon || undefined);
      return true;
    }

    if (cartIcon) {
      cartIcon.click();
      return true;
    }

    return false;
  }

  function setMessage(message, tone = '') {
    const node = qs('[data-qo-role="message"]');
    if (!node) return;
    node.textContent = message || '';
    node.classList.toggle('is-error', tone === 'error');
    node.classList.toggle('is-success', tone === 'success');
    node.classList.toggle('is-loading', tone === 'loading');
  }

  function setStatus(label, tone = '') {
    const node = qs('[data-qo-role="status-chip"]');
    if (!node) return;
    node.textContent = label || 'Ready';
    node.classList.toggle('is-active', tone === 'active');
    node.classList.toggle('is-success', tone === 'success');
    node.classList.toggle('is-error', tone === 'error');
  }

  function parseSkuLines(raw) {
    const output = [];
    String(raw || '').split(/\r?\n/).forEach((original, index) => {
      const line = original.trim();
      if (!line || line.startsWith('#') || line.startsWith('//')) return;

      let sku = '';
      let quantity = 1;
      let match = null;

      match = line.match(/^([A-Z0-9][A-Z0-9_.-]+)\s+qty\s*[:=]\s*(\d+)\s*$/i);
      if (match) {
        sku = match[1];
        quantity = Number(match[2]);
      }

      if (!sku) {
        match = line.match(/^([A-Z0-9][A-Z0-9_.-]+)\s*[x×]\s*(\d+)\s*$/i);
        if (match) {
          sku = match[1];
          quantity = Number(match[2]);
        }
      }

      if (!sku) {
        match = line.match(/^([A-Z0-9][A-Z0-9_.-]+)\s*[\t, ]+\s*(?:x|×)?\s*(\d+)\s*$/i);
        if (match) {
          sku = match[1];
          quantity = Number(match[2]);
        }
      }

      if (!sku) {
        match = line.match(/^([A-Z0-9][A-Z0-9_.-]+)\s*$/i);
        if (match) {
          sku = match[1];
          quantity = 1;
        }
      }

      if (!sku) {
        output.push({
          id: `row-${index}-unparsed`,
          line: index + 1,
          raw: original,
          sku: '',
          quantity: 1,
          status: 'unparsed',
          note: 'Unrecognized format. This row will be skipped.',
        });
        return;
      }

      const normalizedSku = sku.toUpperCase();
      const cleanedFormatting = clean(sku) !== normalizedSku || original !== line;

      output.push({
        id: `row-${index}-${normalizedSku}`,
        line: index + 1,
        raw: original,
        rawSku: sku,
        sku: normalizedSku,
        quantity: Math.max(1, Math.min(99, Number.isFinite(quantity) ? quantity : 1)),
        status: 'ready',
        note: cleanedFormatting ? 'Formatting cleaned up. Review quantity before adding.' : 'Ready for review.',
        cleanedFormatting,
        mergedCount: 1,
      });
    });

    const merged = [];
    const indexBySku = new Map();
    output.forEach((row) => {
      if (!row.sku) {
        merged.push(row);
        return;
      }
      if (indexBySku.has(row.sku)) {
        const existing = merged[indexBySku.get(row.sku)];
        existing.quantity = Math.min(99, existing.quantity + row.quantity);
        existing.mergedCount = Number(existing.mergedCount || 1) + Number(row.mergedCount || 1);
        existing.cleanedFormatting = Boolean(existing.cleanedFormatting || row.cleanedFormatting);
        existing.note = 'Duplicate lines combined. Review quantity before adding.';
        return;
      }
      indexBySku.set(row.sku, merged.length);
      merged.push({ ...row });
    });

    return merged;
  }

  function parseCsv(text) {
    const lines = String(text || '').split(/\r?\n/).filter((line) => line.trim());
    if (!lines.length) return '';
    const header = lines[0].split(',').map((cell) => clean(cell).toLowerCase());
    const hasHeader = header.includes('sku') || header.includes('quantity') || header.includes('qty');
    const skuIndex = hasHeader ? Math.max(0, header.indexOf('sku')) : 0;
    const qtyIndex = hasHeader
      ? (header.indexOf('quantity') >= 0 ? header.indexOf('quantity') : header.indexOf('qty'))
      : 1;
    const dataLines = hasHeader ? lines.slice(1) : lines;

    return dataLines.map((line) => {
      const cells = line.split(',').map((cell) => clean(cell).replace(/^"|"$/g, ''));
      const sku = clean(cells[skuIndex]);
      const qty = qtyIndex >= 0 ? clean(cells[qtyIndex]) : '';
      if (!sku) return '';
      return qty ? `${sku} ${qty}` : sku;
    }).filter(Boolean).join('\n');
  }

  function updateRowsFromInput() {
    const input = qs('[data-qo-role="sku-input"]');
    state.raw = input?.value || '';
    state.rows = parseSkuLines(state.raw);
    state.lastResult = null;
    render();
  }

  function cartCandidateRows() {
    return state.rows.filter((row) => row.sku && row.status !== 'unparsed');
  }

  function skippedRows() {
    return state.rows.filter((row) => !row.sku || ['unparsed', 'missing', 'invalid', 'unavailable', 'cart_failed'].includes(row.status));
  }

  function plural(value, singular, pluralLabel = `${singular}s`) {
    return `${value} ${value === 1 ? singular : pluralLabel}`;
  }

  function skippedReason(row) {
    if (!row.sku || row.status === 'unparsed') return 'unrecognized format';
    if (row.status === 'missing') return 'SKU not found';
    if (row.status === 'invalid') return 'row needs review';
    if (row.status === 'unavailable') return 'not available to add right now';
    if (row.status === 'cart_failed') return 'could not be added';
    return 'needs review';
  }

  function skippedReasonGroups(rows = skippedRows()) {
    return rows.reduce((groups, row) => {
      const reason = skippedReason(row);
      groups.set(reason, (groups.get(reason) || 0) + 1);
      return groups;
    }, new Map());
  }

  function skippedRowsText() {
    return skippedRows()
      .map((row) => clean(row.sku || row.raw))
      .filter(Boolean)
      .join('\n');
  }

  function smartSummaryHtml(rows, skipped) {
    if (!state.rows.length) return '';

    const cleanedCount = rows.filter((row) => row.cleanedFormatting).length;
    const duplicateLinesCombined = rows.reduce((sum, row) => sum + Math.max(0, Number(row.mergedCount || 1) - 1), 0);
    const addedRows = rows.filter((row) => row.status === 'added');
    const addedUnits = addedRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
    const reasonGroups = skippedReasonGroups(skipped);
    const notes = [];

    if (cleanedCount) notes.push(`Cleaned up SKU formatting on ${plural(cleanedCount, 'row')}.`);
    if (duplicateLinesCombined) notes.push(`Combined ${plural(duplicateLinesCombined, 'repeated line')}.`);
    if (addedRows.length) notes.push(`Added ${plural(addedUnits, 'unit')} across ${plural(addedRows.length, 'SKU')}.`);
    else if (rows.length) notes.push(`${plural(rows.length, 'SKU')} ready for review.`);
    notes.push(skipped.length ? 'Rows needing review are skipped, not substituted.' : 'Unknown or unavailable SKUs will be skipped, not substituted.');

    const noteHtml = notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('');
    const reasonHtml = Array.from(reasonGroups.entries())
      .map(([reason, count]) => `<li><strong>${escapeHtml(String(count))}</strong> ${escapeHtml(reason)}</li>`)
      .join('');
    const skippedButton = skipped.length
      ? '<button type="button" class="qo-copy-skipped" data-qo-action="copy-skipped">Copy skipped SKUs</button>'
      : '';

    return `
      <div class="qo-smart">
        <div class="qo-smart__head">
          <strong>Order check</strong>
          <span>${escapeHtml(plural(state.rows.length, 'row'))} parsed</span>
        </div>
        <ul class="qo-smart__list">${noteHtml}</ul>
        ${reasonHtml ? `<div class="qo-smart__review"><span>Needs review</span><ul>${reasonHtml}</ul>${skippedButton}</div>` : ''}
      </div>
    `;
  }

  async function copyTextToClipboard(text) {
    if (!text) return false;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    area.remove();
    return copied;
  }

  async function copySkippedRows() {
    const text = skippedRowsText();
    if (!text) {
      setMessage('No skipped SKUs to copy.', 'success');
      return;
    }
    try {
      await copyTextToClipboard(text);
      setMessage(`Copied ${plural(text.split('\n').length, 'skipped row')} for review.`, 'success');
    } catch (error) {
      console.warn('[quick-order] copy skipped rows failed', error);
      setMessage('Could not copy skipped SKUs from this browser. Select and copy them manually.', 'error');
    }
  }

  function rowStatusGlyph(row) {
    if (row.status === 'added') return '✓';
    if (row.status === 'missing' || row.status === 'invalid' || row.status === 'unavailable' || row.status === 'cart_failed') return '×';
    if (row.status === 'unparsed') return '!';
    if (row.status === 'resolving') return '…';
    return '•';
  }

  function renderRows() {
    const container = qs('[data-qo-role="rows"]');
    const empty = qs('[data-qo-role="empty"]');
    if (!container || !empty) return;

    empty.hidden = state.rows.length > 0;
    container.innerHTML = state.rows.map((row, index) => `
      <div class="qo-row is-${escapeHtml(row.status)}" data-qo-row-index="${index}">
        <span class="qo-row__status" aria-hidden="true">${rowStatusGlyph(row)}</span>
        <span class="qo-row__main">
          <strong class="qo-row__sku">${escapeHtml(row.sku || row.raw || 'Unrecognized row')}</strong>
          <span class="qo-row__note">${escapeHtml(row.note || '')}</span>
        </span>
        <label class="visually-hidden" for="qo-row-qty-${index}">Quantity for ${escapeHtml(row.sku || row.raw)}</label>
        <input id="qo-row-qty-${index}" class="qo-row__qty" type="number" min="1" max="99" step="1" value="${Number(row.quantity || 1)}" data-qo-action="row-qty" data-qo-row-index="${index}" ${!row.sku || state.busy ? 'disabled' : ''}>
        <button type="button" class="qo-row__remove" data-qo-action="remove-row" data-qo-row-index="${index}" aria-label="Remove row" ${state.busy ? 'disabled' : ''}>×</button>
      </div>
    `).join('');
  }

  function renderSummary() {
    const dock = qs('[data-qo-role="dock"]');
    const summary = qs('[data-qo-role="summary"]');
    const warning = qs('[data-qo-role="warning"]');
    const lineCount = qs('[data-qo-role="line-count"]');
    const pulse = document.querySelector('[data-qo-nav-pulse]');

    const rows = cartCandidateRows();
    const skipped = skippedRows();
    const units = rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);

    if (dock) dock.hidden = state.rows.length === 0;
    if (summary) summary.textContent = `${units} unit${units === 1 ? '' : 's'} · ${rows.length} SKU${rows.length === 1 ? '' : 's'}`;
    if (lineCount) {
      lineCount.textContent = `${state.rows.length} row${state.rows.length === 1 ? '' : 's'} · ${rows.length} ready · ${skipped.length} skipped`;
    }
    if (pulse) pulse.hidden = rows.length === 0;

    if (warning) {
      const smartHtml = smartSummaryHtml(rows, skipped);
      warning.hidden = !smartHtml;
      warning.innerHTML = smartHtml;
    }

    const addButton = qs('[data-qo-action="add-to-cart"]') || qs('[data-qo-action="add-to-list"]');
    if (addButton) {
      addButton.disabled = !authorized || state.busy || rows.length === 0;
      addButton.textContent = state.busy ? 'Adding…' : 'Add reviewed rows to cart';
    }

    setStatus(
      state.busy ? 'Checking rows' : rows.length ? `${rows.length} ready` : 'No rows',
      state.busy ? 'active' : rows.length ? 'active' : '',
    );
  }

  function render() {
    renderRows();
    renderSummary();
  }

  function setMode(mode) {
    state.mode = ['sku', 'csv', 'orders'].includes(mode) ? mode : 'sku';
    qsa('[data-qo-view]').forEach((panel) => {
      panel.hidden = panel.dataset.qoView !== state.mode;
    });
    qsa('[data-qo-action="mode"]').forEach((button) => {
      const active = button.dataset.qoMode === state.mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok || payload?.ok === false) {
      const message = payload?.message || payload?.error || `Request failed with status ${response.status}`;
      const error = new Error(message);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  }

  async function resolveRowsForCart(rows) {
    return requestJson(`${proxyBase()}/sku/resolve`, {
      method: 'POST',
      body: JSON.stringify({
        rows: rows.map((row) => ({ sku: row.sku, quantity: row.quantity })),
      }),
    });
  }

  async function addOneResolvedItemToCart(item) {
    return requestJson(`${cartRoot()}cart/add.js`, {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: item.cartVariantId,
            quantity: item.quantity,
          },
        ],
      }),
    });
  }

  async function addResolvedItemsToCart(items) {
    const added = [];
    const failed = [];

    for (const item of items) {
      try {
        await addOneResolvedItemToCart(item);
        added.push(item);
      } catch (error) {
        failed.push({
          ...item,
          errorMessage: error?.payload?.description || error?.payload?.message || error?.message || 'Cart add failed.',
        });
      }
    }

    return { added, failed };
  }

  function mergeResolvedStatuses(result) {
    const bySku = new Map((result?.items || []).map((item) => [clean(item.sku || item.requestedSku).toUpperCase(), item]));

    state.rows = state.rows.map((row) => {
      if (!row.sku) return row;
      const item = bySku.get(row.sku);
      if (!item) {
        return { ...row, status: 'missing', note: 'SKU not found. This row will be skipped.' };
      }
      if (item.status === 'resolved' && item.cartVariantId && item.availableForSale !== false) {
        return { ...row, status: 'resolved', note: 'Ready to add to cart.', cartVariantId: item.cartVariantId };
      }
      if (item.status === 'resolved' && item.availableForSale === false) {
        return { ...row, status: 'unavailable', note: 'Found, but not available to add right now.' };
      }
      if (item.status === 'invalid') {
        return { ...row, status: 'invalid', note: item.reason || 'Review this row before adding.' };
      }
      return { ...row, status: 'missing', note: 'SKU not found. This row will be skipped.' };
    });
  }

  function markCartResult({ added = [], failed = [] } = {}) {
    const addedBySku = new Map(added.map((item) => [clean(item.sku || item.requestedSku).toUpperCase(), item]));
    const failedBySku = new Map(failed.map((item) => [clean(item.sku || item.requestedSku).toUpperCase(), item]));

    state.rows = state.rows.map((row) => {
      if (!row.sku) return row;
      if (addedBySku.has(row.sku)) return { ...row, status: 'added', note: 'Added to cart.' };
      const failedItem = failedBySku.get(row.sku);
      if (failedItem) {
        return {
          ...row,
          status: 'cart_failed',
          note: failedItem.errorMessage || 'Could not add this row to cart.',
        };
      }
      return row;
    });
  }

  function openCartDrawer(trigger) {
    const drawer = document.querySelector('cart-drawer');
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(trigger || cartIcon || undefined);
      return true;
    }
    if (cartIcon) {
      cartIcon.click();
      return true;
    }
    return false;
  }

  function openCartPage() {
    if (!openCartDrawer()) {
      window.location.assign(config.cartUrl || `${cartRoot()}cart`);
    }
  }

  // Reports to sc-dealer-engagement.js (approved dealers only). Nothing happens if it is not loaded.
  function track(name, data) {
    try {
      document.dispatchEvent(new CustomEvent('sc:track', { detail: { name, d: data } }));
    } catch (_error) {
      /* tracking must never break ordering */
    }
  }

  async function addRowsToCart(trigger) {
    if (!authorized) {
      setMessage('Quick Order is available after dealer approval.', 'error');
      return;
    }
    const rows = cartCandidateRows();
    if (!rows.length) {
      setMessage('Paste at least one SKU before adding rows.', 'error');
      return;
    }

    state.busy = true;
    state.rows = state.rows.map((row) => (row.sku ? { ...row, status: 'resolving', note: 'Checking SKU…' } : row));
    setMessage('Checking your SKU list…', 'loading');
    render();

    try {
      const result = await resolveRowsForCart(rows);
      state.lastResult = result;
      mergeResolvedStatuses(result);
      render();

      const resolved = state.rows
        .filter((row) => row.status === 'resolved' && row.cartVariantId)
        .map((row) => ({ sku: row.sku, requestedSku: row.sku, cartVariantId: row.cartVariantId, quantity: row.quantity }));

      const skippedCount = state.rows.filter((row) => ['missing', 'invalid', 'unavailable', 'unparsed'].includes(row.status)).length;
      const unmatched = state.rows.filter((row) => row.sku && ['missing', 'invalid'].includes(row.status));
      track('quick_order_submitted', { mode: state.mode, lines: rows.length, matched: resolved.length, unmatched: unmatched.length });
      unmatched.slice(0, 20).forEach((row) => track('quick_order_sku_unmatched', { term: row.sku, mode: state.mode }));
      if (!resolved.length) {
        setMessage('No reviewed rows could be added. Check skipped rows and try again.', 'error');
        setStatus('Review rows', 'error');
        return;
      }

      setMessage('Adding reviewed rows to cart…', 'loading');
      const cartResult = await addResolvedItemsToCart(resolved);
      markCartResult(cartResult);

      const addedUnitCount = cartResult.added.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const addedSkuCount = cartResult.added.length;
      const failedCount = cartResult.failed.length;
      const totalSkipped = skippedCount + failedCount;

      if (!cartResult.added.length) {
        setMessage('No reviewed rows could be added to cart. Check skipped rows and try again.', 'error');
        setStatus('Review rows', 'error');
        return;
      }

      if (totalSkipped) {
        setMessage(`Added ${addedUnitCount} unit${addedUnitCount === 1 ? '' : 's'} across ${addedSkuCount} SKU${addedSkuCount === 1 ? '' : 's'} to cart. ${totalSkipped} row${totalSkipped === 1 ? '' : 's'} need review. No substitutions were made.`, 'success');
      } else {
        setMessage(`Added ${addedUnitCount} unit${addedUnitCount === 1 ? '' : 's'} across ${addedSkuCount} SKU${addedSkuCount === 1 ? '' : 's'} to cart. No skipped rows.`, 'success');
      }
      setStatus(failedCount ? 'Added with skips' : 'Added to cart', failedCount ? 'active' : 'success');
      const refreshed = await refreshCartUiAfterAdd(trigger);
      if (!refreshed) openCartDrawer(trigger);
    } catch (error) {
      console.warn('[quick-order] cart add failed', error);
      setMessage(error?.message || 'Could not add those rows to cart right now.', 'error');
      setStatus('Error', 'error');
    } finally {
      state.busy = false;
      render();
    }
  }

  function clearRows() {
    state.raw = '';
    state.rows = [];
    state.lastResult = null;
    const input = qs('[data-qo-role="sku-input"]');
    if (input) input.value = '';
    setMessage('');
    render();
  }

  function downloadTemplate() {
    const csv = 'sku,quantity\nLUT-RRD6-6A-WH,2\nGRID-SW8P-US,1\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quick-order-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function importCsvFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ''));
      const skuInput = qs('[data-qo-role="sku-input"]');
      if (skuInput) skuInput.value = parsed;
      setMode('sku');
      updateRowsFromInput();
      setMessage(parsed ? `Imported SKU rows from ${file.name}.` : `No SKU rows found in ${file.name}.`, parsed ? 'success' : 'error');
    };
    reader.onerror = () => setMessage(`Could not read ${file.name}.`, 'error');
    reader.readAsText(file);
  }

  function attachEvents() {
    let inputTimer = null;
    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-qo-role="sku-input"]')) {
        clearTimeout(inputTimer);
        inputTimer = setTimeout(updateRowsFromInput, 200);
        return;
      }

      if (event.target.matches('[data-qo-action="row-qty"]')) {
        const index = Number(event.target.dataset.qoRowIndex);
        if (!Number.isInteger(index) || !state.rows[index]) return;
        state.rows[index].quantity = Math.max(1, Math.min(99, Number(event.target.value || 1)));
        if (['added', 'resolved', 'missing', 'invalid', 'unavailable', 'cart_failed'].includes(state.rows[index].status)) {
          state.rows[index].status = 'ready';
          state.rows[index].note = 'Quantity changed. Review before adding.';
          state.rows[index].cartVariantId = null;
        }
        renderSummary();
      }
    });

    root.addEventListener('click', (event) => {
      const modeButton = event.target.closest('[data-qo-action="mode"]');
      if (modeButton) {
        event.preventDefault();
        setMode(modeButton.dataset.qoMode || 'sku');
        return;
      }

      const formatButton = event.target.closest('[data-qo-action="toggle-format"]');
      if (formatButton) {
        event.preventDefault();
        const guide = qs('[data-qo-role="format-guide"]');
        const expanded = guide?.hidden;
        if (guide) guide.hidden = !expanded;
        formatButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        return;
      }

      const removeButton = event.target.closest('[data-qo-action="remove-row"]');
      if (removeButton) {
        event.preventDefault();
        const index = Number(removeButton.dataset.qoRowIndex);
        if (Number.isInteger(index)) {
          state.rows.splice(index, 1);
          render();
        }
        return;
      }

      if (event.target.closest('[data-qo-action="clear"]')) {
        event.preventDefault();
        clearRows();
        return;
      }

      const cartButton = event.target.closest('[data-qo-action="add-to-cart"], [data-qo-action="add-to-list"]');
      if (cartButton) {
        event.preventDefault();
        addRowsToCart(cartButton);
        return;
      }

      if (event.target.closest('[data-qo-action="open-cart"], [data-qo-action="review-workspace"]')) {
        event.preventDefault();
        openCartPage();
        return;
      }

      if (event.target.closest('[data-qo-action="copy-skipped"]')) {
        event.preventDefault();
        copySkippedRows();
        return;
      }

      if (event.target.closest('[data-qo-action="download-template"]')) {
        event.preventDefault();
        downloadTemplate();
      }
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-qo-role="file-input"]');
      if (!input || !input.files?.length) return;
      importCsvFile(input.files[0]);
      input.value = '';
    });

    const dropzone = qs('[data-qo-role="dropzone"]');
    if (dropzone) {
      ['dragenter', 'dragover'].forEach((name) => {
        dropzone.addEventListener(name, (event) => {
          event.preventDefault();
          dropzone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach((name) => {
        dropzone.addEventListener(name, () => dropzone.classList.remove('is-dragover'));
      });
      dropzone.addEventListener('drop', (event) => {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (!files?.length) return;
        importCsvFile(files[0]);
      });
    }

    document.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        const input = qs('[data-qo-role="sku-input"]');
        if (!input || root.closest('[data-wb-panel]')?.hidden) return;
        event.preventDefault();
        input.focus();
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && document.activeElement?.matches('[data-qo-role="sku-input"]')) {
        event.preventDefault();
        addRowsToCart(document.activeElement);
      }
    });
  }

  function init() {
    if (!authorized) {
      setMessage('Quick Order becomes available once your dealer account is approved.', 'error');
      setStatus('Locked', 'error');
    }
    attachEvents();
    render();
  }

  init();
})();
